<template>
  <div>
  <v-input
      :hint="hint"
      :rules="valid"
      :style="`height: ${chartHeight}px`"
      :value="value"
      persistent-hint
  >
    <v-text-field
        ref="start"
        v-model="start"
        :rules="[rangeValid, startValid]"
        :style="`max-width: ${inputSize}px; min-width: ${inputSize}px;`"
        class="align-to-chart"
        hide-details
        label="Min"
        outlined
        step="any"
        type="number"
    />

    <div class="px-2" style="display: grid; width: 100%;" v-if="chartOptions">
      <fin-chart2 :options="chartOptions" v-if="chartOptions"/>
      <v-range-slider
        v-if="_max && _min && step"
        :color="$finColors.primaryDark"
        :max="_max"
        :min="_min"
        :rules="[ rangeValid ]"
        :step="step"
        :track-color="$finColors.coolGrey"
        :value="sliderRange"
        dense
        hide-details
        style="margin-top: -16px;"
        thumb-label
        @change="sliderChange"
        @start="val => sliderStartVal=val"
      >
        <template #thumb-label="{value}">
          <div class="thumblabel">{{ getSliderLabel(value) }}</div>
        </template>
      </v-range-slider>
    </div>
    <div class="align-to-chart justify-center px-2" style="display: grid; width: 100%;" v-else>
      <p>Keine Daten verfügbar</p>
    </div>


    <v-input>
      <div :style="`height: ${chartHeight}px`"/>
    </v-input>
    <v-text-field
      ref="end"
      v-model="end"
      :rules="[rangeValid, endValid]"
      :style="`max-width: ${inputSize}px; min-width: ${inputSize}px;`"
        class="align-to-chart"
        hide-details
        label="Max"
        outlined
        step="any"
        type="number"
    />
    <v-btn class="ml-sm-5 align-to-chart" icon @click="$emit('delete')">
      <v-icon>icon-delete</v-icon>
    </v-btn>
  </v-input>
  </div>
</template>

<script>

import FinChart2 from "@/components/common/Chart2/FinChart2"

const round = (num) => {
  if (!num) return null
  return Math.round((num + Number.EPSILON) * 100) / 100
}

const formatNumber = (num) => {
  return round(num)?.toLocaleString('de-DE')
}

export default {
  name: 'FinDataSlider',
  components: {FinChart2},
  props: {
    value: {
      type: Array
    },
    openStart: {
      type: Boolean
    },
    openEnd: {
      type: Boolean
    },
    min: {
      type: Number,
    },
    max: {
      type: Number,
    },
    step: {
      type: Number,
      default: 1
    },
    items: {
      type: Array
    },
    chartHeight: {
      type: Number,
      default: 100
    },
    /**
     * Enable/disable chart range selector
     * See https://api.highcharts.com/highstock/rangeSelector.enabled
     */
    rangeSelector: {
      type: Boolean,
      default: false
    }
  },
  data() {
    return {
      sliderStartVal: [null, null],
      startValid: true,
      endValid: true
    }
  },
  methods: {
    formatNumber,
    getSliderLabel(val) {
      if (this.openStart && val === this._min) {
        return '-∞'
      }

      if (this.openEnd && val === this._max) {
        return '∞'
      }
      return formatNumber(val)
    },
    sliderChange(val) {
      //detect changed value
      if (val[0] !== this.sliderStartVal[0]) { //start Changed
        let start = val[0]
        if (this.openStart && start === this._min) {
          start = null
        }
        this.start = round(start)
      }

      if (val[1] !== this.sliderStartVal[1]) { //end Changed
        let end = val[1]
        if (this.openEnd && end === this._max) {
          end = null
        }
        this.end = round(end)
      }
    },
    isInRange(rangeStart, rangeEnd) {
      return (!this.start || rangeStart >= this.start) && (!this.end || rangeStart < this.end)
          || (!this.start || rangeEnd > this.start) && (!this.end || rangeEnd < this.end)
    },
  },
  computed: {
    inputSize() {
      if (this.$vuetify.breakpoint.smAndUp) {
        return 75
      } else return 60.5
    },
    hint() {
      return !this.end && !this.start ? 'Definiere den gewünschten Bereich' : ''
    },
    valid() {
      return [this.rangeValid, this.startValid, this.endValid]
    },
    rangeValid() {
      // console.log('start/end',this.start, this.end  ,this.start <= this.end )
      return !this.end || !this.start || this.start <= this.end || 'Min größer Max'
    },
    _min() {
      return this.openStart && this.step ? this.min - this.step : this.min
    },
    _max() {
      return this.openEnd && this.step ? this.max + this.step : this.max
    },
    _items() {
      if (!this.items || this.items.length === 0) {
        return undefined
      }

      const res = [...this.items]
      if (this.openStart) {
        res.unshift(0)
      }

      if (this.openEnd) {
        res.push(0)
      }

      return res
    },
    sliderRange() {
      return [this.start || this._min, this.end || this._max]
    },
    start: {
      get() {
        return this.value?.length > 0 ? this.value[0] : null
      },
      set(val) {
        if (!val) {
          //in case of empty string set null
          val = null
        } else {
          val = parseFloat(val)
        }

        this.startValid = this.$refs.start?.$el.querySelector('input').validity?.valid || "Min ist eine ungültige Zahl" //Workarround for non numberinput in firefox
        this.$emit('input', [val, this.end])
      }
    },
    end: {
      get() {
        return this.value?.length > 1 ? this.value[1] : null
      },
      set(val) {
        if (!val) {
          //in case of empty string set null
          val = null
        } else {
          val = parseFloat(val)
        }
        this.endValid = this.$refs.end?.$el.querySelector('input').validity?.valid || "Max ist eine ungültige Zahl" //Workarround for non numberinput in firefox
        this.$emit('input', [this.start, val])
      }
    },
    chartOptions() {
      if (!this._items || this._items.length === 0) {
        return undefined
      }
      const step = this.step
      const data = this._items.map((item, index) => {
        const rangeStart = round(this._min + index * step)
        const rangeEnd = round(rangeStart + step)
        const selected = this.isInRange(rangeStart, rangeEnd)
        return {
          name: rangeStart,
          y: item,
          x: rangeStart,
          color: selected ? this.$finColors.primaryDark : this.$finColors.coolGrey
        }
      })
      const res = {
        chart: {
          type: 'column',
          height: this.chartHeight,
          backgroundColor: 'transparent',
          spacingLeft: 0,
          spacingRight: 0,
          spacingTop: 0,
          spacingBottom: 0,
          marginBottom: 0
        },
        title: '',
        credits: {
          enabled: false
        },
        yAxis: {
          visible: false
        },
        xAxis: {
          visible: false
        },
        rangeSelector: {
          enabled: this.rangeSelector
        },
        legend: {
          enabled: false
        },
        tooltip: {
          backgroundColor: this.$finColors.darkGrey,
          style: {
            color: this.$finColors.white
          },
          borderColor: 'lightgray',
          formatter: function () {
            return this.y + ' Einträge für die Werte zwischen '
                + formatNumber(this.x)
                + ' und '
                + formatNumber(this.x + step)
          }
        },
        series: [{
          animation: false,
          data
        }]
      }

      return res
    }
  }
}
</script>

<style lang="scss" scoped>
.align-to-chart {
  margin-top: 30px;
}

.thumblabel {
  background-color: rgb(2, 7, 58);
  border-radius: 5px;

  .error & {
    background-color: #ff5252;
  }
}

::v-deep {
  .v-slider__thumb {
    width: 23px;
    height: 23px;
    box-shadow: $fin-color-shadow;
    background: radial-gradient(circle, rgba(2, 7, 58, 1) 1%, rgba(2, 7, 58, 1) 37%, rgba(255, 255, 255, 1) 37%, rgba(222, 228, 236, 1) 100%) !important;
    border: solid 1px #e0e2e3 !important;
    cursor: pointer;
    margin-left: -6px;

    &:before {
      display: none;
    }
  }

  .v-messages__message {
    margin-top: -16px;
  }
}

@media only screen and (max-width: 600px) {
  ::v-deep .v-input, ::v-deep fieldset {
    max-height: 45px;
  }
}
</style>
